import { useState } from "react";
import { Button, Icons, Surface } from "@/components/ui";
import { cn } from "@/lib/cn";
import { LiquidDenkerMark } from "@/workshop/components/canvas/liquid-denker-mark";
import { AgentCursor } from "@/workshop/components/cursors/agent-cursor";
import { WorkshopLayoutProvider } from "@/workshop/components/layout/layout-provider";
import { WorkshopMasterLayout } from "@/workshop/components/layout/master-layout";

type IntroMode = "sequence" | "final";

interface WorkSquare {
  id: string;
  title: string;
  meta: string;
  className: string;
  tone: "green" | "blue" | "violet" | "pink";
}

const WORK_SQUARES: WorkSquare[] = [
  {
    id: "brief",
    title: "Product Brief",
    meta: "Denker is arranging the workspace",
    className: "left-[13%] top-[17%] h-60 w-[34rem] rotate-[-2deg]",
    tone: "green",
  },
  {
    id: "agents",
    title: "Agent Team",
    meta: "Coder, Researcher, Designer",
    className: "right-[12%] top-[19%] h-64 w-[25rem] rotate-[2deg]",
    tone: "blue",
  },
  {
    id: "plan",
    title: "Execution Plan",
    meta: "Tasks queue into place",
    className: "bottom-[17%] left-[26%] h-56 w-[26rem] rotate-[1deg]",
    tone: "violet",
  },
  {
    id: "memory",
    title: "Memory",
    meta: "Context warms up",
    className: "bottom-[19%] right-[19%] h-52 w-[21rem] rotate-[-1deg]",
    tone: "pink",
  },
];

function toneClassName(tone: WorkSquare["tone"]) {
  if (tone === "green") return "from-sky-200/28 via-sky-300/10 to-transparent";
  if (tone === "blue") return "from-sky-300/35 via-sky-300/10 to-transparent";
  if (tone === "violet") return "from-violet-300/35 via-violet-300/10 to-transparent";
  return "from-pink-300/35 via-pink-300/10 to-transparent";
}

function WorkSquareTile({ square }: { square: WorkSquare }) {
  return (
    <div
      className={cn(
        "denker-intro-work-square absolute overflow-hidden rounded-[28px]",
        "border border-glass-stroke-light bg-glass-fill shadow-glass backdrop-blur-glass-sm",
        square.className,
      )}
      data-testid={`denker-intro-square-${square.id}`}
    >
      <div
        className={cn(
          "pointer-events-none absolute -inset-1 bg-gradient-to-br opacity-70 blur-2xl",
          toneClassName(square.tone),
        )}
      />
      <div
        className={cn(
          "denker-intro-square-rim pointer-events-none absolute inset-0 rounded-[28px]",
          "border border-white/40 opacity-0",
        )}
      />
      <div className="denker-intro-square-scan pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-sky-200 to-transparent opacity-0" />
      <div className="denker-intro-square-scan pointer-events-none absolute inset-x-8 bottom-0 h-px bg-gradient-to-r from-transparent via-sky-300 to-transparent opacity-0" />
      <div className="relative flex h-full flex-col justify-between p-5">
        <div>
          <div className="mb-4 h-2 w-20 rounded-full bg-white/18" />
          <h3 className="text-appkit-body font-semibold text-primary">
            {square.title}
          </h3>
          <p className="mt-1 text-appkit-caption text-muted">{square.meta}</p>
        </div>
        <div className="grid grid-cols-[1.1fr_0.8fr] gap-3">
          <div className="h-24 rounded-[18px] border border-white/[0.06] bg-white/[0.04]" />
          <div className="space-y-2">
            <div className="h-7 rounded-full bg-white/[0.06]" />
            <div className="h-7 rounded-full bg-white/[0.04]" />
            <div className="h-7 rounded-full bg-white/[0.035]" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MockWorkspaceCanvas() {
  return (
    <div
      className="relative h-full w-full overflow-hidden bg-black"
      data-testid="denker-first-open-canvas"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[length:32px_32px] opacity-60" />
      <div className="absolute inset-0 bg-[radial-gradient(900px_480px_at_50%_36%,rgba(125,211,252,0.11),transparent_62%),radial-gradient(760px_420px_at_76%_32%,rgba(96,165,250,0.12),transparent_58%),radial-gradient(720px_460px_at_42%_76%,rgba(167,139,250,0.1),transparent_60%)]" />
      {WORK_SQUARES.map((square) => (
        <WorkSquareTile key={square.id} square={square} />
      ))}
    </div>
  );
}

function DenkerDockTarget({ mode }: { mode: IntroMode }) {
  return (
    <div
      className={cn(
        "pointer-events-none absolute left-1/2 top-9 z-40 -translate-x-1/2",
        mode === "sequence" ? "denker-intro-dock-arrival" : "opacity-100",
      )}
      data-testid="denker-intro-dock-target"
    >
      <Surface
        surfaceRole="rail"
        nativeLevel="root"
        nativeGroup="denker-intro-dock"
        className="relative h-10 w-10 rounded-full bg-transparent shadow-none"
      >
        <CompactDenkerBubble
          markSize={20}
          testId="denker-intro-dock-bubble"
          className="h-10 w-10"
        />
      </Surface>
    </div>
  );
}

function CompactDenkerBubble({
  markSize,
  testId,
  className,
  animated = false,
}: {
  markSize: number;
  testId: string;
  className?: string;
  animated?: boolean;
}) {
  return (
    <div
      className={cn(
        "denker-intro-compact-bubble relative grid place-items-center overflow-hidden rounded-full",
        animated && "denker-intro-compact-bubble-active",
        className,
      )}
      data-testid={testId}
    >
      <span className="denker-intro-bubble-reflect pointer-events-none absolute -inset-x-10 -inset-y-8" />
      <span className="denker-intro-bubble-core pointer-events-none absolute inset-[16%] rounded-full" />
      <LiquidDenkerMark
        size={markSize}
        className="denker-intro-liquid-mark relative z-10 drop-shadow-[0_8px_20px_rgba(0,0,0,0.22)]"
        testId={`${testId}-logo`}
      />
    </div>
  );
}

function IntroCompactBubble({ mode }: { mode: IntroMode }) {
  if (mode === "final") return null;

  return (
    <div
      className="pointer-events-none absolute left-1/2 top-1/2 z-50 h-28 w-28 -translate-x-1/2 -translate-y-1/2"
      data-testid="denker-intro-center-bubble"
    >
      <div className="denker-intro-logo-flight absolute inset-0">
        <CompactDenkerBubble
          animated
          markSize={58}
          testId="denker-intro-liquid-logo"
          className="h-full w-full"
        />
      </div>
    </div>
  );
}

function CursorIntro({ mode }: { mode: IntroMode }) {
  return (
    <div
      className={cn(
        "pointer-events-none absolute bottom-28 left-[calc(50%+44px)] z-50",
        mode === "sequence" ? "denker-intro-cursor-arrival" : "opacity-100",
      )}
      data-testid="denker-intro-cursor-stage"
    >
      <AgentCursor
        agentName="Denker"
        agentColor="#7DD3FC"
        content="Hi, I am Denker, your AI coworker."
        data-testid="denker-intro-agent-cursor"
      />
      <div
        className={cn(
          "ml-8 mt-2 w-max rounded-[18px] border border-glass-stroke-light",
          "bg-glass-fill-heavy px-3 py-2 shadow-glass-sm backdrop-blur-glass-sm",
        )}
        data-testid="denker-intro-name-tag"
      >
        <p className="text-appkit-caption font-semibold leading-none text-primary">
          Denker
        </p>
        <p className="mt-1 text-appkit-mini font-medium leading-none text-sky-200">
          AI Coworker
        </p>
      </div>
    </div>
  );
}

function IntroDimLayer({ mode }: { mode: IntroMode }) {
  if (mode === "final") return null;

  return (
    <div
      className="denker-intro-dim pointer-events-none absolute inset-0 z-30"
      data-testid="denker-intro-dim-layer"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-[5px]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(12,18,28,0.06)_0%,rgba(0,0,0,0.18)_30%,rgba(0,0,0,0.84)_82%)]" />
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-sky-300/12 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-sky-300/10 to-transparent" />
    </div>
  );
}

function IntroControls({
  onReplay,
  onFinal,
}: {
  onReplay: () => void;
  onFinal: () => void;
}) {
  return (
    <div
      className="pointer-events-auto absolute right-5 top-5 z-[60] flex items-center gap-2"
      data-testid="denker-intro-controls"
    >
      <Surface
        surfaceRole="toolbar"
        nativeLevel="root"
        nativeGroup="denker-intro-controls"
        className="flex items-center gap-1.5 rounded-pill border border-glass-stroke-light p-1 shadow-glass-sm"
      >
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onReplay}
          data-testid="denker-intro-replay"
        >
          <Icons.Play className="h-3.5 w-3.5" />
          Replay
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onFinal}
          data-testid="denker-intro-final-state"
        >
          <Icons.MousePointer className="h-3.5 w-3.5" />
          End state
        </Button>
      </Surface>
    </div>
  );
}

function IntroStage({
  mode,
  onReplay,
  onFinal,
}: {
  mode: IntroMode;
  onReplay: () => void;
  onFinal: () => void;
}) {
  return (
    <div className="absolute inset-0" data-testid="denker-intro-stage">
      <IntroDimLayer mode={mode} />
      <DenkerDockTarget mode={mode} />
      <IntroCompactBubble mode={mode} />
      <CursorIntro mode={mode} />
      <IntroControls onReplay={onReplay} onFinal={onFinal} />
      <style>
        {`
          @keyframes denker-intro-frame-glow {
            0%, 100% { box-shadow: var(--shadow-glass); filter: brightness(0.92); }
            28% { box-shadow: 0 0 0 1px rgba(125,211,252,0.42), 0 0 60px rgba(125,211,252,0.2), var(--shadow-glass); filter: brightness(1.12); }
            58% { box-shadow: 0 0 0 1px rgba(96,165,250,0.44), 0 0 68px rgba(96,165,250,0.2), var(--shadow-glass); filter: brightness(1.1); }
            82% { box-shadow: 0 0 0 1px rgba(255,255,255,0.24), 0 0 36px rgba(125,211,252,0.12), var(--shadow-glass); }
          }

          @keyframes denker-intro-square-rim {
            0%, 13% { opacity: 0; box-shadow: inset 0 0 0 0 rgba(125,211,252,0); }
            26% { opacity: 0.8; box-shadow: inset 0 0 0 1px rgba(125,211,252,0.48), 0 0 28px rgba(125,211,252,0.2); }
            62% { opacity: 0.78; box-shadow: inset 0 0 0 1px rgba(96,165,250,0.5), 0 0 32px rgba(96,165,250,0.24); }
            100% { opacity: 0.2; box-shadow: inset 0 0 0 1px rgba(255,255,255,0.16), 0 0 18px rgba(255,255,255,0.08); }
          }

          @keyframes denker-intro-square-scan {
            0%, 14% { opacity: 0; transform: translateX(-22%); }
            30% { opacity: 1; transform: translateX(4%); }
            64% { opacity: 0.8; transform: translateX(20%); }
            100% { opacity: 0; transform: translateX(34%); }
          }

          @keyframes denker-intro-dim {
            0%, 58% { opacity: 1; }
            78% { opacity: 0.72; }
            100% { opacity: 0; }
          }

          @keyframes denker-intro-logo-flight {
            0% { opacity: 0; transform: translate3d(0, 24px, 0) scale(0.72); filter: blur(8px); }
            12% { opacity: 1; transform: translate3d(0, 0, 0) scale(1.02); filter: blur(0); }
            30% { transform: translate3d(0, -3px, 0) scale(1); }
            58% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
            74% { opacity: 1; transform: translate3d(0, -18vh, 0) scale(0.82); }
            88% { opacity: 0.98; transform: translate3d(0, calc(-50vh + 56px), 0) scale(0.42); }
            100% { opacity: 0; transform: translate3d(0, calc(-50vh + 56px), 0) scale(0.36); }
          }

          @keyframes denker-intro-logo-reflect {
            0%, 100% { transform: translateX(-18%) skewX(-8deg); }
            50% { transform: translateX(18%) skewX(8deg); }
          }

          @keyframes denker-intro-bubble-core {
            0%, 100% { opacity: 0.18; transform: scale(0.92); }
            50% { opacity: 0.34; transform: scale(1.08); }
          }

          @keyframes denker-intro-compact-breathe {
            0%, 100% { filter: brightness(1) saturate(1); }
            50% { filter: brightness(1.12) saturate(1.12); }
          }

          @keyframes denker-intro-liquid-mark {
            0%, 100% { opacity: 0.95; transform: translateY(0) scale(1); filter: saturate(1.06) brightness(1); }
            48% { opacity: 1; transform: translateY(-2px) scale(1.015); filter: saturate(1.18) brightness(1.12); }
          }

          @keyframes denker-intro-dock-arrival {
            0%, 69% { opacity: 0; transform: translateX(-50%) scale(0.8); }
            80% { opacity: 1; transform: translateX(-50%) scale(1.18); }
            100% { opacity: 1; transform: translateX(-50%) scale(1); }
          }

          @keyframes denker-intro-cursor-arrival {
            0%, 78% { opacity: 0; transform: translate3d(-72px, -68px, 0) scale(0.8); }
            90% { opacity: 1; transform: translate3d(8px, 4px, 0) scale(1.04); }
            100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
          }

          .denker-intro-work-square {
            animation: denker-intro-frame-glow 6.4s cubic-bezier(0.16, 1, 0.3, 1) both;
          }

          .denker-intro-square-rim {
            animation: denker-intro-square-rim 6.4s cubic-bezier(0.16, 1, 0.3, 1) both;
          }

          .denker-intro-square-scan {
            animation: denker-intro-square-scan 5.8s cubic-bezier(0.16, 1, 0.3, 1) both;
          }

          .denker-intro-dim {
            animation: denker-intro-dim 5.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }

          .denker-intro-logo-flight {
            animation: denker-intro-logo-flight 5.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }

          .denker-intro-compact-bubble {
            --denker-intro-mark-hi: var(--color-readable-overlay-primary);
            --denker-intro-mark-soft: var(--color-readable-overlay-secondary);
            --denker-intro-mark-mid: color-mix(in srgb, var(--color-readable-overlay-secondary) 72%, var(--color-readable-overlay-primary) 28%);
            background:
              radial-gradient(circle at 34% 24%, color-mix(in srgb, var(--color-readable-overlay-primary) 22%, rgba(255,255,255,0.34)), transparent 34%),
              linear-gradient(145deg, color-mix(in srgb, var(--color-readable-overlay-fill) 18%, rgba(47,58,72,0.94)), color-mix(in srgb, var(--color-readable-overlay-fill-hover) 16%, rgba(20,26,36,0.94)));
            border: 1px solid color-mix(in srgb, var(--color-readable-overlay-primary) 12%, rgba(255,255,255,0.14));
            box-shadow:
              inset 0 1px 14px color-mix(in srgb, var(--color-readable-overlay-primary) 22%, rgba(255,255,255,0.06)),
              inset 0 -12px 24px color-mix(in srgb, var(--color-readable-overlay-secondary) 16%, rgba(0,0,0,0.24)),
              0 10px 32px rgba(0,0,0,0.22);
            backdrop-filter: blur(18px) saturate(1.28);
          }

          .denker-intro-compact-bubble-active {
            animation: denker-intro-compact-breathe 4.8s ease-in-out infinite;
          }

          .denker-intro-bubble-reflect {
            opacity: 0.58;
            background: linear-gradient(108deg, transparent 0%, color-mix(in srgb, var(--color-readable-overlay-secondary) 42%, transparent) 24%, color-mix(in srgb, var(--color-readable-overlay-primary) 36%, transparent) 43%, color-mix(in srgb, var(--color-readable-overlay-secondary) 32%, transparent) 58%, transparent 92%);
            animation: denker-intro-logo-reflect 5.8s ease-in-out infinite;
          }

          .denker-intro-bubble-core {
            background: radial-gradient(circle, color-mix(in srgb, var(--color-readable-overlay-primary) 22%, transparent), transparent 68%);
            animation: denker-intro-bubble-core 3.2s ease-in-out infinite;
          }

          .denker-intro-liquid-mark {
            animation: denker-intro-liquid-mark 4.6s ease-in-out infinite;
            transform-origin: center;
          }

          .denker-intro-dock-arrival {
            animation: denker-intro-dock-arrival 6.1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }

          .denker-intro-cursor-arrival {
            animation: denker-intro-cursor-arrival 7.1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }

          @media (prefers-reduced-motion: reduce) {
            .denker-intro-work-square,
            .denker-intro-square-rim,
            .denker-intro-square-scan,
            .denker-intro-dim,
            .denker-intro-logo-flight,
            .denker-intro-compact-bubble-active,
            .denker-intro-bubble-reflect,
            .denker-intro-bubble-core,
            .denker-intro-liquid-mark,
            .denker-intro-dock-arrival,
            .denker-intro-cursor-arrival {
              animation-duration: 1ms;
              animation-iteration-count: 1;
            }
          }
        `}
      </style>
    </div>
  );
}

export function DenkerFirstOpenPage() {
  const [mode, setMode] = useState<IntroMode>("sequence");
  const [sequenceKey, setSequenceKey] = useState(0);

  function replay() {
    setMode("sequence");
    setSequenceKey((current) => current + 1);
  }

  function showFinal() {
    setMode("final");
    setSequenceKey((current) => current + 1);
  }

  return (
    <WorkshopLayoutProvider defaultSidebarOpen defaultRightPanelOpen>
      <div
        className="relative h-screen w-screen overflow-hidden"
        data-testid="denker-first-open-page"
      >
        <WorkshopMasterLayout>
          <MockWorkspaceCanvas key={sequenceKey} />
        </WorkshopMasterLayout>
        <IntroStage
          key={sequenceKey}
          mode={mode}
          onReplay={replay}
          onFinal={showFinal}
        />
      </div>
    </WorkshopLayoutProvider>
  );
}
